import { React, useState } from "react";
import {
	Box,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {
	Dashboard,
	QueryStats,
	Paid,
	ShowChart,
	Calculate,
	Settings,
	Logout,
	Category,
	MenuOpen,
	Help,
} from "@mui/icons-material";
import { NavLink, useLocation } from "react-router-dom";
import ThemeSwitcher from "../common/ThemeSwitcher";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";

function Navigation() {
	const isMobile = useMediaQuery("(max-width: 600px)");
	const location = useLocation();
	const theme = useTheme();
	const { t } = useTranslation();
	const { logout } = useAuth();

	const [isMenuOpen, setIsMenuOpen] = useState(true);

	const topMenuItems = [
		{
			id: "dashboard",
			text: t("dashboard"),
			icon: <Dashboard />,
			path: "/dashboard",
			startPath: "/dashboard",
		},
		{
			id: "manager",
			text: t("manager"),
			icon: <QueryStats />,
			path: "/manager/nettworth",
			startPath: "/manager",
		},
		{
			id: "transactions",
			text: t("transactions"),
			icon: <Paid />,
			path: "/transactions",
			startPath: "/transactions",
		},
		{
			id: "categories",
			text: t("categories"),
			icon: <Category />,
			path: "/categories",
			startPath: "/categories",
		},
		{
			id: "investments",
			text: t("investments"),
			icon: <ShowChart />,
			path: "/investments",
			startPath: "/investments",
		},
		{
			id: "calculators",
			text: t("calculators"),
			icon: <Calculate />,
			path: "/calculators",
			startPath: "/calculators",
		},
	];

	const bottomMenuItems = [
		{
			id: "help",
			text: t("help"),
			icon: <Help />,
			path: "/help",
			startPath: "/help",
		},
		{
			id: "settings",
			text: t("settings"),
			icon: <Settings />,
			path: "/settings",
			startPath: "/settings",
		},
		{
			id: "logout",
			text: t("logout"),
			icon: <Logout />,
			onClick: logout,
		},
	];

	const drawerStyles = {
		width: isMobile ? "100vw" : isMenuOpen ? 240 : 50,
		maxWidth: isMobile ? "100vw" : isMenuOpen ? 240 : 50,
		[`& .MuiDrawer-paper`]: {
			backgroundColor: theme.palette.background.default,
			width: isMobile ? "100vw" : isMenuOpen ? 240 : 50,
			border: "none",
			maxWidth: isMobile ? "100vw" : isMenuOpen ? 240 : 50,
			height: isMobile ? "min(70px, 10vh)" : "100vh",
			display: "flex",
			flexDirection: isMobile ? "row" : "column",
			justifyContent: isMobile ? "space-around" : "flex-start",
		},
	};

	const listItemStyles = (startPath) => ({
		display: "flex",
		flexDirection: isMobile ? "column" : "row",
		alignItems: "center",
		justifyContent: isMobile ? "center" : "left",
		backgroundColor: location.pathname.startsWith(startPath)
			? theme.palette.active.main
			: theme.palette.background.default,
		borderRadius: "10px",
		color: theme.palette.text.primary,
		"&:hover": { backgroundColor: "action.hover" },
		pt: isMobile ? 1 : 0.5,
		pb: isMobile ? 0 : 0.5,
		px: isMenuOpen ? 2 : 1,
	});

	const listItemIconStyles = {
		minWidth: "auto",
		mr: 1,
		display: "flex",
		justifyContent: "center",
		py: isMenuOpen ? 0 : 0.5,
	};

	const listItemTextStyles = {
		flexGrow: 0,
		"& .MuiTypography-root": {
			fontSize: isMobile && "14px",
		},
	};

	const toggleMenuState = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	return (
		<>
			<Drawer
				variant="permanent"
				open={isMenuOpen}
				anchor={isMobile ? "bottom" : "left"}
				sx={drawerStyles}
			>
				<Toolbar
					sx={{
						display: "flex",
						padding: "0 !important",
					}}
				>
					<Box
						sx={{
							flexGrow: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							pl: isMenuOpen ? 3 : 1,
							flexDirection: isMenuOpen
								? "row"
								: "column-reverse",
						}}
					>
						{!isMobile && isMenuOpen && (
							<>
								<Typography variant="h6" sx={{ flexGrow: 1 }}>
									FinApp
								</Typography>
								<ThemeSwitcher />
							</>
						)}
						<Tooltip
							title={
								isMenuOpen
									? t("hideSideMenu")
									: t("showSideMenu")
							}
							placement="right"
							arrow
						>
							<IconButton
								sx={{ borderRadius: "10px", p: 1 }}
								size="small"
								onClick={toggleMenuState}
							>
								<MenuOpen
									sx={{
										transform: !isMenuOpen
											? "rotate(180deg)"
											: "",
									}}
								/>
							</IconButton>
						</Tooltip>
					</Box>
				</Toolbar>
				<Box
					sx={{
						display: "flex",
						flexGrow: "1",
						flexDirection: isMobile ? "row" : "column",
						justifyContent: "space-between",
						width: "100%",
						gap: isMobile ? 0 : 0.5,
						pt: 1,
						pr: 0,
						pl: 1,
						mb: 2,
					}}
				>
					<List>
						{topMenuItems.map((item) => (
							<Tooltip
								key={item.id}
								title={isMenuOpen ? null : item.text}
								placement="right"
								arrow
							>
								<ListItem
									component={NavLink}
									to={item.path}
									sx={listItemStyles(item.startPath)}
								>
									<ListItemIcon sx={listItemIconStyles}>
										{item.icon}
									</ListItemIcon>
									{isMenuOpen && (
										<ListItemText
											primary={item.text}
											sx={listItemTextStyles}
										/>
									)}
								</ListItem>
							</Tooltip>
						))}
					</List>
					<List>
						{!isMenuOpen && <ThemeSwitcher />}
						{bottomMenuItems.map((item) => (
							<Tooltip
								key={item.id}
								title={isMenuOpen ? null : item.text}
								placement="right"
								arrow
							>
								<ListItem
									component={NavLink}
									to={item.path}
									sx={listItemStyles(item.startPath)}
									onClick={item.onClick}
								>
									<ListItemIcon sx={listItemIconStyles}>
										{item.icon}
									</ListItemIcon>
									{isMenuOpen && (
										<ListItemText
											primary={item.text}
											sx={listItemTextStyles}
										/>
									)}
								</ListItem>
							</Tooltip>
						))}
					</List>
				</Box>
			</Drawer>
		</>
	);
}

export default Navigation;
